import React, { useState } from "react";
import { KeyRound, X, Copy, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/** Modal shown when an admin clicks "Reset password" on a user row.
 *  Two modes:
 *    - Auto-generate: leave field blank → backend returns a 12-char password
 *    - Manual: type a new password (>= 6 chars)
 *  After success, the new password is displayed prominently with a Copy button
 *  so the admin can share it with the user via Slack / verbally.
 *  The password is shown ONCE — closing the modal loses it forever.
 */
export default function ResetPasswordModal({ user, onClose, onDone }) {
  const [manualMode, setManualMode] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (manualMode && password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const body = manualMode ? { new_password: password } : {};
      const { data } = await api.post(`/users/${user.id}/reset-password`, body);
      setResult(data);
      toast.success(`Password reset for ${user.email}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const copyPw = async () => {
    try {
      await navigator.clipboard.writeText(result.new_password);
      setCopied(true);
      toast.success("Password copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Clipboard write failed:", e);
      toast.error("Couldn't access clipboard — select & copy manually");
    }
  };

  const close = () => {
    if (result && onDone) onDone();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      {!result ? (
        <form
          onSubmit={submit}
          data-testid="reset-password-modal"
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
              Reset password
            </h3>
            <button
              type="button"
              onClick={close}
              className="text-slate-400 hover:text-slate-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-slate-600 mb-3">
            Reset the password for <strong>{user.email}</strong>. The user keeps their existing
            session — you&apos;ll need to share the new password with them.
          </p>

          <div className="flex gap-2 mb-3 text-xs font-semibold">
            <button
              type="button"
              data-testid="reset-mode-auto"
              onClick={() => setManualMode(false)}
              className={`px-3 py-1.5 rounded-md ${
                !manualMode
                  ? "bg-orange-50 text-orange-700 border border-orange-200"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              Auto-generate
            </button>
            <button
              type="button"
              data-testid="reset-mode-manual"
              onClick={() => setManualMode(true)}
              className={`px-3 py-1.5 rounded-md ${
                manualMode
                  ? "bg-orange-50 text-orange-700 border border-orange-200"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              I&apos;ll enter one
            </button>
          </div>

          {manualMode && (
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                New password
              </label>
              <input
                required
                minLength={8}
                type="text"
                data-testid="reset-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none font-mono"
              />
            </div>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={close}
              className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="reset-submit-btn"
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 text-white text-sm font-semibold"
            >
              <KeyRound className="w-4 h-4" />
              {busy ? "Resetting…" : "Reset Password"}
            </button>
          </div>
        </form>
      ) : (
        <div
          data-testid="reset-result-modal"
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
              Password reset complete
            </h3>
            <button onClick={close} className="text-slate-400 hover:text-slate-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-slate-600 mb-3">
            Share this password with <strong>{result.user_email}</strong>. It&apos;s only shown once.
          </p>
          <div className="flex items-stretch gap-2">
            <input
              readOnly
              value={result.new_password}
              data-testid="reset-result-password"
              onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-300 bg-slate-50 text-sm font-mono text-slate-900 outline-none focus:border-orange-400"
            />
            <button
              data-testid="reset-copy-btn"
              onClick={copyPw}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition ${
                copied
                  ? "bg-emerald-500 text-white"
                  : "bg-slate-900 hover:bg-slate-800 text-white"
              }`}
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={close}
              data-testid="reset-result-close"
              className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
